<template>
  <!-- ICONIC: ein Benutzer mit Rolle, Kanal-Rechten und Sonderrechten in einfachen Worten -->
  <v-container class="user-detail">
    <v-btn text small class="px-1 mb-2" :to="{ name: 'users' }">
      <v-icon left>mdi-arrow-left</v-icon>Alle Benutzer
    </v-btn>

    <v-skeleton-loader
      v-if="loading && !user.name"
      type="article, list-item-three-line, list-item-three-line"
    ></v-skeleton-loader>

    <template v-else-if="user.name">
      <!-- Kopf -->
      <div class="d-flex align-center mb-4">
        <v-badge
          dot
          bordered
          overlap
          :color="online ? 'success' : 'grey'"
          class="mr-4"
        >
          <v-avatar size="48" color="secondary" class="white--text text-h6">
            {{ initial }}
          </v-avatar>
        </v-badge>
        <div class="min-w-0">
          <h1 class="text-h5 font-weight-medium text-truncate">
            {{ user.name }}
            <v-icon v-if="locked" color="warning">mdi-lock</v-icon>
          </h1>
          <div class="text-body-2 text--secondary">
            {{ online ? `Gerade online in ${online.channel}` : `Zuletzt da: ${lastSeen}` }}
          </div>
        </div>
      </div>

      <v-alert v-if="locked" type="warning" outlined dense icon="mdi-lock">
        <b>Geschützt.</b> {{ user.name }} kann in dieser Oberfläche niemand
        ändern, rauswerfen oder bannen, auch du nicht.
      </v-alert>

      <!-- Kurz gesagt -->
      <v-card outlined class="mb-4">
        <v-card-title class="text-subtitle-1 font-weight-medium pb-1"
          >Kurz gesagt</v-card-title
        >
        <v-card-text class="text-body-1">
          <ul class="summary">
            <li v-for="(line, i) in summary" :key="i" v-html="line"></li>
          </ul>
        </v-card-text>
      </v-card>

      <!-- Rolle auf dem Server -->
      <v-card outlined class="mb-4">
        <v-card-title class="text-subtitle-1 font-weight-medium pb-0"
          >Rolle auf dem Server</v-card-title
        >
        <v-card-text>
          <v-radio-group
            :key="radioKey"
            :value="roleKey"
            class="mt-2"
            hide-details
            :disabled="locked || saving"
            @change="askServerRole"
          >
            <v-radio
              v-for="role in groups.serverRoles"
              :key="role.key"
              :value="role.key"
              class="align-start mb-3"
            >
              <template #label>
                <div>
                  <div class="font-weight-medium text--primary">
                    {{ role.label }}
                  </div>
                  <div class="text-body-2">{{ role.text }}</div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
          <div v-if="extraGroups.length" class="text-body-2 mt-2">
            Außerdem in: {{ extraGroups.join(", ") }}
          </div>
        </v-card-text>
      </v-card>

      <!-- Rechte in Kanälen -->
      <v-card outlined class="mb-4">
        <v-card-title class="text-subtitle-1 font-weight-medium pb-1">
          Rechte in Kanälen
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            small
            :disabled="locked || saving"
            @click="openChannelDialog()"
          >
            <v-icon left small>mdi-plus</v-icon>Kanal-Rechte geben
          </v-btn>
        </v-card-title>
        <v-card-text>
          <p v-if="roleKey === 'admin'" class="text-body-2 mb-2">
            Als Server Admin hat {{ user.name }} in allen Kanälen ohnehin alle
            Rechte. Kanal-Rollen ändern daran nichts.
          </p>
          <p v-if="!channelRoles.length" class="text-body-2 mb-0">
            Keine besonderen Rechte in einzelnen Kanälen.
          </p>
          <v-list v-else class="py-0">
            <template v-for="(entry, i) in channelRoles">
              <v-divider v-if="i" :key="`d${entry.cid}`"></v-divider>
              <v-list-item :key="entry.cid" class="px-0">
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">{{
                    entry.path
                  }}</v-list-item-title>
                  <v-list-item-subtitle>{{ entry.label }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="flex-row">
                  <v-btn
                    text
                    small
                    :disabled="locked || saving"
                    @click="openChannelDialog(entry)"
                    >Ändern</v-btn
                  >
                  <v-btn
                    text
                    small
                    color="error"
                    :disabled="locked || saving"
                    @click="askRemoveChannelRole(entry)"
                    >Wegnehmen</v-btn
                  >
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
        </v-card-text>
      </v-card>

      <!-- Sonderrechte -->
      <v-card outlined class="mb-4">
        <v-card-title class="text-subtitle-1 font-weight-medium pb-0"
          >Sonderrechte auf dem ganzen Server</v-card-title
        >
        <v-card-text>
          <p class="text-body-2 mt-1 mb-2">
            Direkt für {{ user.name }}, ohne Rolle. Wirken nur bei Normalen und
            Gästen. Server Admins und SuperAdmin sind dafür zu stark.
          </p>
          <v-switch
            v-for="right in specialRights"
            :key="right.perm"
            :input-value="right.on"
            :disabled="locked || saving"
            inset
            hide-details
            class="mt-3"
            @change="toggleSpecial(right, $event)"
          >
            <template #label>
              <div>
                <span class="text--primary">{{ right.label }}</span>
                <span class="text-body-2 ml-1">({{ right.text }})</span>
                <span
                  v-if="right.on && right.value !== powerDefault"
                  class="text-caption ml-1"
                  >Stärke {{ right.value }}</span
                >
              </div>
            </template>
          </v-switch>
        </v-card-text>
      </v-card>

      <div class="text-caption text--secondary mb-8">
        Zum ersten Mal da: {{ firstSeen }} · {{ user.connections }} Besuche ·
        Datenbank-ID {{ cldbid }} ·
        <router-link
          :to="{ name: 'permissions-client', params: { cldbid } }"
          class="text--secondary"
          >alle Einzelrechte (für Profis)</router-link
        >
      </div>
    </template>

    <v-alert v-else-if="!loading" type="error" outlined>
      Diesen Benutzer gibt es nicht (mehr).
    </v-alert>

    <!-- Kanal-Rechte geben oder ändern -->
    <v-dialog v-model="channelDialog.open" max-width="560" scrollable>
      <v-card>
        <v-card-title>{{
          channelDialog.fixedCid ? "Kanal-Rolle ändern" : "Kanal-Rechte geben"
        }}</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="channelDialog.cid"
            :items="channelItems"
            item-text="path"
            item-value="cid"
            label="Kanal"
            outlined
            dense
            :disabled="Boolean(channelDialog.fixedCid)"
            no-data-text="Kein Kanal gefunden"
          ></v-autocomplete>
          <v-radio-group v-model="channelDialog.role" class="mt-0" hide-details>
            <v-radio
              v-for="role in groups.channelRoles"
              :key="role.key"
              :value="role.key"
              class="align-start mb-3"
            >
              <template #label>
                <div>
                  <div class="font-weight-medium text--primary">
                    {{ role.label }}
                  </div>
                  <div class="text-body-2">{{ role.text }}</div>
                  <div
                    v-if="role.key === 'chef' && !role.cgid"
                    class="text-caption mt-1"
                  >
                    Diese Rolle gibt es in TeamSpeak noch nicht. Sie wird beim
                    Speichern einmalig angelegt.
                  </div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="channelDialog.open = false">Abbrechen</v-btn>
          <v-btn
            color="primary"
            depressed
            :disabled="!channelDialog.cid || !channelDialog.role"
            :loading="saving"
            @click="saveChannelRole"
            >Speichern</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Rückfrage -->
    <v-dialog v-model="confirm.open" max-width="480">
      <v-card>
        <v-card-title>{{ confirm.title }}</v-card-title>
        <v-card-text class="text-body-1" v-html="confirm.text"></v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirm.open = false">Abbrechen</v-btn>
          <v-btn
            :color="confirm.danger ? 'error' : 'primary'"
            depressed
            :loading="saving"
            @click="runConfirm"
            >{{ confirm.button }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  loadConfig,
  ensureServer,
  asList,
  resolveGroups,
  serverRoleOf,
  channelRoleLabel,
  channelPaths,
  ensureKanalChef,
  SPECIAL_RIGHTS,
  SPECIAL_POWER,
  formatDate,
  relativeDate,
  errorText,
} from "@/iconic/rechte";

const esc = (s) =>
  String(s == null ? "" : s).replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[
        c
      ])
  );

export default {
  data() {
    return {
      cldbid: String(this.$route.params.cldbid),
      loading: true,
      saving: false,
      locked: false,
      user: {},
      groups: { serverRoles: [], channelRoles: [] },
      sgids: [],
      assignments: [],
      perms: {},
      channels: [],
      online: null,
      powerDefault: SPECIAL_POWER,
      radioKey: 0,
      channelDialog: { open: false, cid: null, role: null, fixedCid: null },
      confirm: {
        open: false,
        title: "",
        text: "",
        button: "",
        danger: false,
        action: null,
      },
    };
  },
  computed: {
    initial() {
      return (String(this.user.name || "?").trim()[0] || "?").toUpperCase();
    },
    lastSeen() {
      return relativeDate(this.user.lastConnected);
    },
    firstSeen() {
      return formatDate(this.user.created);
    },
    role() {
      return serverRoleOf(this.groups, this.sgids);
    },
    roleKey() {
      return this.role ? this.role.key : null;
    },
    extraGroups() {
      const standard = this.groups.serverRoles.map((r) => r.sgid);
      return this.sgids
        .filter((s) => !standard.includes(s))
        .map((s) => this.groups.sgName(s))
        .filter(Boolean);
    },
    pathById() {
      const map = {};
      this.channels.forEach((c) => (map[c.cid] = c.path));
      return map;
    },
    channelItems() {
      return this.channels;
    },
    channelRoles() {
      return this.assignments
        .filter((a) => String(a.cgid) !== this.groups.defaultCg)
        .map((a) => ({
          cid: String(a.cid),
          cgid: String(a.cgid),
          path: this.pathById[String(a.cid)] || `Kanal ${a.cid}`,
          label: channelRoleLabel(this.groups, a.cgid),
        }))
        .sort((a, b) => a.path.localeCompare(b.path));
    },
    specialRights() {
      return SPECIAL_RIGHTS.map((r) => {
        const p = this.perms[r.perm];
        const value = p ? Number(p.permvalue) : 0;
        return { ...r, on: value > 0, value };
      });
    },
    summary() {
      const name = esc(this.user.name);
      const lines = [];
      if (this.role)
        lines.push(`<b>${esc(this.role.label)}</b>: ${esc(this.role.short)}.`);
      if (this.roleKey !== "admin") {
        this.channelRoles.forEach((r) =>
          lines.push(`<b>${esc(r.label)}</b> in «${esc(r.path)}».`)
        );
        const on = this.specialRights.filter((r) => r.on);
        if (on.length)
          lines.push(
            `Darf überall: ${on
              .map((r) => esc(r.label))
              .join(", ")} (nur bei Normalen und Gästen).`
          );
        lines.push(
          "Kann Server Admins und SuperAdmin nicht rauswerfen, verschieben oder bannen."
        );
      }
      if (this.locked)
        lines.push(`${name} ist geschützt und hier nicht änderbar.`);
      return lines;
    },
  },
  methods: {
    async load() {
      this.loading = true;
      const ts = this.$TeamSpeak;
      const id = this.cldbid;
      try {
        await ensureServer();
        const [
          config,
          serverInfo,
          serverGroups,
          channelGroups,
          channels,
          dbInfo,
          sgOfUser,
          assignments,
          perms,
          clients,
        ] = await Promise.all([
          loadConfig(),
          ts.execute("serverinfo").then((r) => asList(r)[0]),
          ts.execute("servergrouplist").then(asList),
          ts.execute("channelgrouplist").then(asList),
          ts.execute("channellist").then(asList),
          ts.execute("clientdbinfo", { cldbid: id }).then((r) => asList(r)[0]),
          ts.execute("servergroupsbyclientid", { cldbid: id }).then(asList),
          ts.execute("channelgroupclientlist", { cldbid: id }).then(asList),
          ts
            .execute("clientpermlist", { cldbid: id }, ["-permsid"])
            .then(asList),
          ts.execute("clientlist").then(asList),
        ]);
        this.locked = config.protectedDbIds.includes(id);
        this.groups = resolveGroups(serverInfo, serverGroups, channelGroups);
        this.channels = channelPaths(channels);
        this.user = {
          name: dbInfo.clientNickname,
          created: dbInfo.clientCreated,
          lastConnected: dbInfo.clientLastconnected,
          connections: dbInfo.clientTotalconnections,
        };
        this.sgids = sgOfUser.map((g) => String(g.sgid));
        this.assignments = assignments;
        const map = {};
        perms.forEach((p) => (map[p.permsid] = p));
        this.perms = map;
        const here = clients.find(
          (c) =>
            String(c.clientDatabaseId) === id && String(c.clientType) === "0"
        );
        this.online = here
          ? { channel: this.pathById[String(here.cid)] || "" }
          : null;
      } catch (err) {
        this.user = {};
        this.$toast.error(errorText(err));
      }
      this.loading = false;
    },

    // Rückfrage-Dialog
    ask(title, text, button, action, danger = false) {
      this.confirm = { open: true, title, text, button, action, danger };
    },
    async runConfirm() {
      const action = this.confirm.action;
      await this.run(action);
      this.confirm.open = false;
    },
    async run(action, done = "Gespeichert.") {
      this.saving = true;
      try {
        await action();
        this.$toast.success(done);
      } catch (err) {
        this.$toast.error(errorText(err));
      }
      await this.load();
      this.saving = false;
    },

    // Rolle auf dem Server
    askServerRole(key) {
      const target = this.groups.serverRoles.find((r) => r.key === key);
      if (!target || key === this.roleKey) return;
      const name = esc(this.user.name);
      const before = this.roleKey;
      // Auswahl zeigt bis zur Bestätigung weiter die alte Rolle
      this.radioKey += 1;
      if (key === "admin") {
        this.ask(
          "Zum Server Admin machen?",
          `<b>${name}</b> darf dann fast alles auf dem Server: Leute rauswerfen, verschieben und bannen, Rollen und Rechte ändern. Nur geben, wenn du der Person voll vertraust.`,
          "Ja, Server Admin",
          () => this.setServerRole("admin")
        );
      } else if (before === "admin") {
        this.ask(
          "Server Admin wegnehmen?",
          `<b>${name}</b> wird ${esc(target.label)} und verliert alle Admin-Rechte.`,
          `Ja, ${target.label}`,
          () => this.setServerRole(key),
          true
        );
      } else {
        this.run(() => this.setServerRole(key));
      }
    },
    async setServerRole(key) {
      const cldbid = this.cldbid;
      const sg = (k) =>
        (this.groups.serverRoles.find((r) => r.key === k) || {}).sgid;
      const has = (sgid) => sgid && this.sgids.includes(sgid);
      const add = (sgid) =>
        sgid && !has(sgid)
          ? this.$TeamSpeak.execute("servergroupaddclient", { sgid, cldbid })
          : null;
      const del = (sgid) =>
        has(sgid)
          ? this.$TeamSpeak.execute("servergroupdelclient", { sgid, cldbid })
          : null;
      if (key === "admin") {
        await add(sg("admin"));
      } else if (key === "normal") {
        await add(sg("normal"));
        await del(sg("admin"));
      } else {
        await del(sg("admin"));
        await del(sg("normal"));
      }
    },

    // Kanal-Rollen
    openChannelDialog(entry) {
      const role = entry
        ? this.groups.channelRoles.find((r) => r.cgid === entry.cgid)
        : null;
      this.channelDialog = {
        open: true,
        cid: entry ? entry.cid : null,
        fixedCid: entry ? entry.cid : null,
        role: role ? role.key : "chef",
      };
    },
    saveChannelRole() {
      const { cid, role: key } = this.channelDialog;
      const role = this.groups.channelRoles.find((r) => r.key === key);
      this.channelDialog.open = false;
      this.run(async () => {
        const cgid =
          key === "chef" ? await ensureKanalChef(this.groups) : role.cgid;
        await this.$TeamSpeak.execute("setclientchannelgroup", {
          cgid,
          cid,
          cldbid: this.cldbid,
        });
      });
    },
    askRemoveChannelRole(entry) {
      this.ask(
        "Kanal-Rechte wegnehmen?",
        `<b>${esc(this.user.name)}</b> ist dann in «${esc(
          entry.path
        )}» kein ${esc(entry.label)} mehr.`,
        "Wegnehmen",
        () =>
          this.$TeamSpeak.execute("setclientchannelgroup", {
            cgid: this.groups.defaultCg,
            cid: entry.cid,
            cldbid: this.cldbid,
          }),
        true
      );
    },

    // Sonderrechte
    toggleSpecial(right, on) {
      const cldbid = this.cldbid;
      this.run(
        () =>
          on
            ? this.$TeamSpeak.execute("clientaddperm", {
                cldbid,
                permsid: right.perm,
                permvalue: SPECIAL_POWER,
                permskip: 1,
              })
            : this.$TeamSpeak.execute("clientdelperm", {
                cldbid,
                permsid: right.perm,
              }),
        on ? `${right.label}: an` : `${right.label}: aus`
      );
    },
  },
  created() {
    this.load();
  },
  async beforeRouteUpdate(to, from, next) {
    this.cldbid = String(to.params.cldbid);
    this.user = {};
    next();
    await this.load();
  },
};
</script>

<style scoped>
.user-detail {
  max-width: 880px;
}
.min-w-0 {
  min-width: 0;
}
.summary {
  padding-left: 1.1em;
}
.summary li + li {
  margin-top: 4px;
}
</style>
